import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { graphql } from 'gatsby';
import { BLOCKS } from '@contentful/rich-text-types';
import { Section } from '~components/Section';
import { Heading } from '~components/Heading';
import { Modal } from '~components/Modal';
import { useIntersectionObserver } from '~components/IntersectionObserver';
import { RichTextRenderer } from '~components/RichTextRenderer';
import { ComponentCoverageListItem } from './ComponentCoverageListItem';
import Chevron from './assets/chevron.svg';

import * as styles from './ComponentCoverageList.module.css';

import type { RichTextRendererProps } from '~components/RichTextRenderer';
import type { ContentBlock } from '~components/types';
import type { ContentfulComponentCoverageList } from './types';

type ComponentCoverageListProps = {
  content: ContentfulComponentCoverageList;
};

export const ComponentCoverageList: ContentBlock<ComponentCoverageListProps> = ({
  content: {
    heading,
    subheading,
    items,
    modalButtonLabelHeading,
    modalButtonLabelDescription,
    modalContentInclusions,
    modalContentExclusions,
  },
  ...sectionProps
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const { entry, observe, unobserve } = useIntersectionObserver(headingRef, { threshold: 1 });
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const onClose = useCallback(() => {
    setModalOpen(false);
  }, [modalOpen]);

  useEffect(() => {
    if (!headingRef.current) return;
    observe();
    if (entry?.isIntersecting) unobserve();
  }, [entry?.isIntersecting]);

  return (
    <Section {...sectionProps} className={styles.section}>
      {subheading && <strong className={styles.subheading}>{subheading}</strong>}
      <Heading className={styles.heading} ref={headingRef} level={1}>
        {heading}
      </Heading>
      <ul className={styles.list}>
        {items.map((item, index) => (
          <ComponentCoverageListItem
            key={index}
            content={item}
            className={clsx(styles.listItem, entry?.isIntersecting && styles.reveal)}
            style={
              {
                '--index': index,
              } as React.CSSProperties
            }
          />
        ))}
        <li
          className={clsx(styles.listItem, styles.modalButton, entry?.isIntersecting && styles.reveal)}
          style={
            {
              '--index': items.length,
            } as React.CSSProperties
          }
          onClick={() => setModalOpen(true)}
        >
          <Heading className={styles.modalButtonLabelHeading} level={2}>
            {modalButtonLabelHeading}
            <div className={styles.chevronWrapper}>
              <Chevron className={styles.chevron} />
            </div>
          </Heading>
          <div className={styles.modalButtonLabelDescription}>{modalButtonLabelDescription}</div>
        </li>
      </ul>
      <Modal open={modalOpen} onClose={onClose} className={styles.modal}>
        <ModalRichTextRenderer content={modalContentInclusions.raw} className={styles.inclusions} />
        <ModalRichTextRenderer content={modalContentExclusions.raw} className={styles.exclusions} />
      </Modal>
    </Section>
  );
};

const ModalRichTextRenderer: React.FC<RichTextRendererProps> = ({ content, className }) => (
  <RichTextRenderer
    content={content}
    wrapperElement='div'
    className={clsx(styles.modalRichText, className)}
    options={{
      renderNode: {
        [BLOCKS.HEADING_2]: (_, children) => (
          <Heading className={styles.modalRichTextHeading} level={2}>
            {children}
          </Heading>
        ),
        [BLOCKS.UL_LIST]: (_, children) => <ul className={styles.modalRichTextlist}>{children}</ul>,
        [BLOCKS.LIST_ITEM]: (_, children) => <li className={styles.modalRichTextlistItem}>{children}</li>,
      },
    }}
  />
);

export const query = graphql`
  fragment ComponentCoverageList on ContentfulComponentCoverageList {
    __typename
    heading
    subheading
    items {
      ...ComponentCoverageListItem
    }
    modalButtonLabelHeading
    modalButtonLabelDescription
    modalContentInclusions {
      raw
    }
    modalContentExclusions {
      raw
    }
    theme
    blockLayout
  }
`;
